import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Section } from '../../chat/fanBlocks'
import { useSectionsOpen } from '../../chat/sectionsOpen'
import { FeatherIcon } from '../../components/FeatherIcon'
import { Spinner } from '../../components/Spinner'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { useMediaSrc } from '../../library/useMediaSrc'
import { asList, formatUsd, toMs, unwrap, type Attachment, type FanslyAccount, type Playable } from '../data'
import { useFanslyApi } from '../deps'
import { toFanslyError } from '../useFanslyQuery'
import { FsThumb, FsViewer } from '../ui/FsMedia'
import { fanslyProfileUrl, initialsOf } from './format'
import { ACCOUNT_NOTE, fanListsOf, GROUP_MEDIA_LOCATION, noteFromAnswer, notesOf, offersOf, spendOf, type ChatRow, type FanList, type FanNote, type FanSpend, type SpendKind } from './model'

/** Earnings pages asked for one fan (100 rows each). */
const SPEND_PAGES = 5
/** Gallery items per page. */
export const GALLERY_PAGE = 30

interface Props {
  row: ChatRow
  /** The fan's account as Fansly sent it (with the creator's notes on them), when known. */
  fan: FanslyAccount | null
  meId: string
  can: { writeChats: boolean; readLists: boolean; writeLists: boolean; readStats: boolean }
  /** Desktop: a column beside the chat; phones: inside the Fan sheet. */
  sheet: boolean
  onHide: () => void
  notify: (message: string) => void
}

function lastSeen(ms: number, locale: string, t: (k: string) => string): string {
  if (!ms) return '—'
  if (Date.now() - ms < 5 * 60_000) return t('fan.now')
  const d = new Date(ms)
  return `${d.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`
}

function Identity({ row, fan, onHide, sheet }: Pick<Props, 'row' | 'fan' | 'onHide' | 'sheet'>) {
  const { t, i18n } = useTranslation('fanslyChats')
  const pic = useMediaSrc(fan?.avatar || row.avatar)
  const name = fan?.name || row.name || row.username
  const yes = (v: boolean) => (v ? t('fan.yes') : t('fan.no'))
  return (
    <div className="rc-fan-id">
      <div className="rc-fan-who">
        <span className="rc-avatar rc-avatar--40" ref={pic.ref as React.RefObject<HTMLSpanElement>}>
          {pic.src ? <img src={pic.src} alt="" /> : initialsOf(name || '?')}
        </span>
        <div className="rc-fan-names">
          <span className="rc-fan-name">{name || t('list.fan')}</span>
          {row.username && (
            <a className="rc-muted fsc-profile-link" href={fanslyProfileUrl(row.username)} target="_blank" rel="noopener noreferrer">
              @{row.username}
            </a>
          )}
        </div>
        <button type="button" className="rc-icon-btn" title={sheet ? t('close') : t('fan.hide')} aria-label={sheet ? t('close') : t('fan.hide')} onClick={onHide}>
          <FeatherIcon name={sheet ? 'x' : 'panel-right-close'} size={14} />
        </button>
      </div>
      <div className="rc-fan-tiles">
        {[
          [t('fan.followsYou'), yes(fan?.followsYou ?? row.followsYou)],
          [t('fan.subscriber'), yes(row.subscriber || !!fan?.subscriber)],
          [t('fan.lastSeen'), lastSeen(toMs(fan?.raw.lastSeenAt ?? row.account?.raw.lastSeenAt), i18n.language, t)],
        ].map(([label, value]) => (
          <div key={label} title={`${label}: ${value}`}>
            <span>{label}</span>
            <b>{value}</b>
          </div>
        ))}
      </div>
    </div>
  )
}

function ListsBlock({ fanId, canWrite, sections, notify }: { fanId: string; canWrite: boolean; sections: ReturnType<typeof useSectionsOpen>; notify: (m: string) => void }) {
  const { t } = useTranslation('fanslyChats')
  const api = useFanslyApi()
  const [lists, setLists] = useState<FanList[] | null>(null)
  const [error, setError] = useState('')
  const [picker, setPicker] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [removing, setRemoving] = useState<FanList | null>(null)

  useEffect(() => {
    let live = true
    api
      .get('/crm/lists', { itemId: fanId })
      .then((p) => live && setLists(fanListsOf(p, fanId)))
      .catch((e: unknown) => live && setError(toFanslyError(e).message))
    return () => {
      live = false
    }
  }, [api, fanId])

  const set = (id: string, member: boolean) => setLists((prev) => prev?.map((l) => (l.id === id ? { ...l, member, count: Math.max(0, l.count + (member ? 1 : -1)) } : l)) ?? prev)

  const toggle = async (l: FanList, member: boolean) => {
    setBusy(l.id)
    set(l.id, member)
    try {
      if (member) await api.post('/crm/lists/items/add', { listItems: [{ id: fanId, listId: l.id, type: 1 }] })
      else await api.post('/crm/lists/items/remove', { listId: l.id, listItemIds: [fanId], confirm: true })
    } catch (e) {
      set(l.id, !member)
      notify(toFanslyError(e).message)
    } finally {
      setBusy(null)
    }
  }

  const on = lists?.filter((l) => l.member) ?? []
  return (
    <Section id="fsc-lists" icon="list" label={t('fan.lists')} summary={lists ? String(on.length) : ''} sections={sections}>
      {lists === null && !error && <Spinner size={14} label={t('loading')} />}
      {error && <span className="rc-error">{error}</span>}
      {lists && (
        <div className="rc-fan-lists">
          {on.map((l) => (
            <span key={l.id} className="rc-list-chip" translate="no">
              {l.name}
            </span>
          ))}
          {!on.length && !canWrite && <span className="rc-muted">{t('fan.noLists')}</span>}
          {canWrite && (
            <button type="button" className="rc-list-chip rc-list-chip--add" title={t('fan.editLists')} aria-label={t('fan.editLists')} aria-expanded={picker} onClick={() => setPicker((v) => !v)}>
              <FeatherIcon name={picker ? 'x' : 'plus'} size={12} />
            </button>
          )}
        </div>
      )}
      {picker && lists && (
        <div className="rc-list-picker">
          <div className="rc-list-options">
            {lists.map((l) => (
              <button key={l.id} type="button" className={`rc-list-option ${l.member ? 'is-on' : ''}`} aria-pressed={l.member} disabled={busy === l.id} onClick={() => (l.member ? setRemoving(l) : void toggle(l, true))}>
                <span className="rc-check">{l.member && <FeatherIcon name="check" size={10} />}</span>
                <span translate="no">{l.name}</span>
                <span className="rc-muted">{l.count}</span>
              </button>
            ))}
            {!lists.length && <span className="rc-muted">{t('fan.noListsYet')}</span>}
          </div>
        </div>
      )}
      {removing && (
        <ConfirmPopover
          title={t('fan.removeTitle')}
          body={t('fan.removeBody', { list: removing.name })}
          confirmLabel={t('fan.remove')}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            const l = removing
            setRemoving(null)
            void toggle(l, false)
          }}
        />
      )}
    </Section>
  )
}

const SPEND_ORDER: SpendKind[] = ['tips', 'media', 'bundles', 'subscriptions', 'locked', 'other']

function SpendBlock({ fanId, sections }: { fanId: string; sections: ReturnType<typeof useSectionsOpen> }) {
  const { t, i18n } = useTranslation('fanslyChats')
  const api = useFanslyApi()
  const [spend, setSpend] = useState<FanSpend | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    let live = true
    ;(async () => {
      const all: ReturnType<typeof asList> = []
      const seen = new Set<string>()
      for (let page = 0; page < SPEND_PAGES; page += 1) {
        const rowsList = asList(unwrap(await api.get('/crm/creator/earnings/account-stats', { correlationAccountId: fanId, before: 0, after: 0, limit: 100, offset: page * 100 })))
        let added = 0
        for (const r of rowsList) {
          const key = `${String(r.timestamp)}:${String(r.type)}`
          if (seen.has(key)) continue
          seen.add(key)
          all.push(r)
          added += 1
        }
        if (rowsList.length < 100 || !added) break
      }
      return spendOf(all)
    })()
      .then((s) => live && setSpend(s))
      .catch((e: unknown) => live && setError(toFanslyError(e).message))
    return () => {
      live = false
    }
  }, [api, fanId])
  const money = (n: number) => formatUsd(Math.round(n * 100) / 100, i18n.language)
  return (
    <Section id="fsc-spend" icon="dollar-sign" label={t('fan.spent')} summary={spend ? money(spend.total) : ''} sections={sections}>
      {!spend && !error && <Spinner size={14} label={t('loading')} />}
      {error && <span className="rc-error">{error}</span>}
      {spend && (
        <div className="rc-fan-grid">
          <span>{t('fan.total')}</span>
          <b>{money(spend.total)}</b>
          {SPEND_ORDER.filter((k) => spend.byKind[k]).map((k) => (
            <FragmentRow key={k} label={t(`spend.${k}`)} value={money(spend.byKind[k] ?? 0)} />
          ))}
          <span>{t('fan.net')}</span>
          <span>{money(spend.net)}</span>
        </div>
      )}
    </Section>
  )
}

function FragmentRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <span>{label}</span>
      <span>{value}</span>
    </>
  )
}

function GalleryBlock({ groupId, fanId, meId, sections }: { groupId: string; fanId: string; meId: string; sections: ReturnType<typeof useSectionsOpen> }) {
  const { t } = useTranslation('fanslyChats')
  const api = useFanslyApi()
  const [who, setWho] = useState<'fan' | 'me'>('fan')
  const [items, setItems] = useState<Attachment[]>([])
  const [next, setNext] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [viewing, setViewing] = useState<Playable | null>(null)
  const owner = who === 'me' ? meId : fanId

  const load = useCallback(
    async (before: string, reset: boolean) => {
      if (!owner) return
      setLoading(true)
      setError('')
      try {
        const page = offersOf(
          await api.get('/crm/chats/media', { locationId: groupId, locationType: GROUP_MEDIA_LOCATION, accountId: owner, mediaType: '', ...(before ? { before } : {}), after: 0, limit: GALLERY_PAGE, offset: 0 }),
          GALLERY_PAGE,
        )
        setItems((prev) => (reset ? page.items : [...prev, ...page.items]))
        setNext(page.next)
      } catch (e) {
        setError(toFanslyError(e).message)
      } finally {
        setLoading(false)
      }
    },
    [api, groupId, owner],
  )

  useEffect(() => {
    setItems([])
    setNext('')
    void load('', true)
  }, [load])

  const tiles = items.flatMap((a) =>
    a.type === 'bundle'
      ? [{ key: `b${a.bundle.id}`, media: a.bundle.preview, price: a.bundle.price, purchased: a.bundle.purchased, count: a.bundle.items.length, open: a.bundle.items[0]?.media ?? a.bundle.preview }]
      : [{ key: `m${a.item.id}`, media: a.item.media ?? a.item.preview, price: a.item.price, purchased: a.item.purchased, count: 0, open: a.item.media }],
  )

  return (
    <Section id="fsc-media" icon="image" label={t('fan.media')} summary={tiles.length ? String(tiles.length) + (next ? '+' : '') : ''} sections={sections}>
      {meId && (
        <div className="fsc-seg" role="tablist" aria-label={t('fan.media')}>
          {(['fan', 'me'] as const).map((w) => (
            <button key={w} type="button" role="tab" aria-selected={who === w} className={`lib-chip ${who === w ? 'is-on' : ''}`} onClick={() => setWho(w)}>
              {t(`fan.mediaFrom.${w}`)}
            </button>
          ))}
        </div>
      )}
      {error && <span className="rc-error">{error}</span>}
      {!!tiles.length && (
        <div className="fsc-gallery">
          {tiles.map((x) => (
            <div key={x.key} className="fsc-gallery-item">
              <FsThumb media={x.media} price={x.price} count={x.count || undefined} onOpen={x.open ? () => setViewing(x.open) : undefined} />
              {x.price > 0 && who === 'me' && x.purchased && <span className="fsc-gallery-state is-bought">{t('thread.bought')}</span>}
            </div>
          ))}
        </div>
      )}
      {loading && <Spinner size={16} label={t('loading')} />}
      {!loading && !error && !tiles.length && <span className="rc-muted">{who === 'me' ? t('fan.noMediaMe') : t('fan.noMediaFan')}</span>}
      {!loading && next && (
        <button type="button" className="rc-btn fsc-more" onClick={() => void load(next, false)}>
          {t('fan.moreMedia')}
        </button>
      )}
      {viewing && <FsViewer media={viewing} onClose={() => setViewing(null)} />}
    </Section>
  )
}

function NotesBlock({ fanId, initial, canWrite, sections, notify }: { fanId: string; initial: FanNote[]; canWrite: boolean; sections: ReturnType<typeof useSectionsOpen>; notify: (m: string) => void }) {
  const { t, i18n } = useTranslation('fanslyChats')
  const api = useFanslyApi()
  const [notes, setNotes] = useState<FanNote[]>(initial)
  const [editing, setEditing] = useState<FanNote | 'new' | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState<FanNote | null>(null)

  // Notes that come with the fan's account (they may arrive after the chat opened).
  useEffect(() => {
    setNotes((prev) => {
      const ids = new Set(initial.map((n) => n.id))
      return [...initial, ...prev.filter((n) => !ids.has(n.id))]
    })
  }, [initial])

  const start = (n: FanNote | 'new') => {
    setEditing(n)
    setTitle(n === 'new' ? '' : n.title)
    setBody(n === 'new' ? '' : n.note)
  }

  const save = async () => {
    if (!body.trim()) return
    setBusy(true)
    try {
      if (editing === 'new') {
        const answer = await api.post('/crm/notes/create', { contentType: ACCOUNT_NOTE, contentId: fanId, title: title.trim(), note: body.trim() })
        setNotes((prev) => [noteFromAnswer(answer, { title: title.trim(), note: body.trim(), fanId }), ...prev])
      } else if (editing) {
        await api.post('/crm/notes/edit', { id: editing.id, contentType: ACCOUNT_NOTE, contentId: fanId, title: title.trim(), note: body.trim() })
        setNotes((prev) => prev.map((n) => (n.id === editing.id ? { ...n, title: title.trim(), note: body.trim() } : n)))
      }
      setEditing(null)
    } catch (e) {
      notify(toFanslyError(e).message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (n: FanNote) => {
    const before = notes
    setNotes((prev) => prev.filter((x) => x.id !== n.id))
    try {
      await api.post('/crm/notes/delete', { id: n.id, contentType: ACCOUNT_NOTE, contentId: fanId, title: n.title, note: n.note, confirm: true })
    } catch (e) {
      setNotes(before)
      notify(toFanslyError(e).message)
    }
  }

  return (
    <Section
      id="fsc-notes"
      icon="file-text"
      label={t('notes.title')}
      summary={notes.length ? String(notes.length) : ''}
      tools={
        canWrite && editing === null ? (
          <button type="button" className="rc-icon-btn" title={t('notes.add')} aria-label={t('notes.add')} onClick={() => start('new')}>
            <FeatherIcon name="plus" size={14} />
          </button>
        ) : undefined
      }
      sections={sections}
    >
      {editing !== null && (
        <div className="fsc-note-form">
          <input className="fsc-input" value={title} placeholder={t('notes.titlePlaceholder')} aria-label={t('notes.titleLabel')} onChange={(e) => setTitle(e.target.value)} />
          <textarea className="fsc-textarea" rows={3} value={body} placeholder={t('notes.placeholder')} aria-label={t('notes.label')} autoFocus onChange={(e) => setBody(e.target.value)} />
          <div className="fsc-note-actions">
            <button type="button" className="rc-btn" onClick={() => setEditing(null)} disabled={busy}>
              {t('cancel')}
            </button>
            <button type="button" className="rc-btn rc-btn--primary" disabled={busy || !body.trim()} onClick={() => void save()}>
              {busy ? <Spinner size={12} label={t('saving')} /> : t('notes.save')}
            </button>
          </div>
        </div>
      )}
      {!notes.length && editing === null && <span className="rc-muted">{t('notes.none')}</span>}
      {notes.map((n) => (
        <div key={n.id} className="fsc-note">
          <div className="fsc-note-head">
            <b>{n.title || t('notes.untitled')}</b>
            {n.createdAt > 0 && <span className="rc-muted">{new Date(n.createdAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}</span>}
            {canWrite && (
              <span className="fsc-note-tools">
                <button type="button" className="rc-icon-btn" title={t('notes.edit')} aria-label={t('notes.edit')} onClick={() => start(n)}>
                  <FeatherIcon name="edit-2" size={12} />
                </button>
                <button type="button" className="rc-icon-btn" title={t('notes.delete')} aria-label={t('notes.delete')} onClick={() => setDeleting(n)}>
                  <FeatherIcon name="trash-2" size={12} />
                </button>
              </span>
            )}
          </div>
          <p className="fsc-note-text">{n.note}</p>
        </div>
      ))}
      {deleting && (
        <ConfirmPopover
          title={t('notes.deleteTitle')}
          body={t('notes.deleteBody')}
          confirmLabel={t('notes.delete')}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const n = deleting
            setDeleting(null)
            void remove(n)
          }}
        />
      )}
    </Section>
  )
}

/**
 * The fan beside the chat (§8): who they are, their lists, what they spent,
 * the media in this chat (theirs, or what this creator sent them with whether
 * it was bought) and the creator's notes on them.
 */
export function SideColumn({ row, fan, meId, can, sheet, onHide, notify }: Props) {
  const sections = useSectionsOpen()
  const source = (fan ?? row.account)?.raw
  const initialNotes = useMemo(() => notesOf(source), [source])
  const body = (
    <>
      <Identity row={row} fan={fan} onHide={onHide} sheet={sheet} />
      <div className="rc-side-top rc-fan-scroll">
        {can.readLists && <ListsBlock fanId={row.fanId} canWrite={can.writeLists} sections={sections} notify={notify} />}
        {can.readStats && <SpendBlock fanId={row.fanId} sections={sections} />}
        <GalleryBlock groupId={row.groupId} fanId={row.fanId} meId={meId} sections={sections} />
        <NotesBlock fanId={row.fanId} initial={initialNotes} canWrite={can.writeChats} sections={sections} notify={notify} />
      </div>
    </>
  )
  if (sheet) return <div className="rc-side rc-side--sheet rc-side--phone fsc-side">{body}</div>
  return <aside className="rc-fan rc-side fsc-side">{body}</aside>
}
